/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore } from 'pinia'
import { computed, ref, toRaw } from 'vue'
import type {
  OriginShapeData,
  TaskDataLength,
  TaskForUpdate,
  UserDefectInputItem,
  UserDefectInputPreset,
  UserSettingConfig,
} from '../data/type'
import _, { cloneDeep } from 'lodash'
import { isApp } from '@/index'
import { useWebEditorDataStore, useWebEditorFacilityStore } from './webeditor'
import { APIServer } from '../data/api'
import { useShapeStore } from '../data/Editor/shape'
import { useDefectPhotoStore } from '../data/Editor/defectPhoto'
import { useDeviceEventLogStore } from './deviceEventLog'
import { App } from '..'
import {
  isPreviousPhoto,
  isPreviousShape,
  mergeServerToLocalServerFirst,
  setLastInSync,
  sortByKeys,
} from '../util'
import { Network } from '@capacitor/network'
import { CloudFile } from '@/lib/CloudFile'
import { useAppStateStore } from './state'
import { useCanvasStore } from './canvas'
import { useDocStore } from '../data/Editor/docs'
import SQLite from '../../lib/Task/sqlite'
import {
  Defect,
  Facility,
  FacilityPhoto,
  FieldsUpdatedAt,
  Floorplan,
  Task,
  DefectPhoto,
  DefectDetailItem,
} from '../data/types'
import Merge from '../../lib/Task/merge'
import { useFloorplanStore } from '../data/Editor/floorplan'
import { useFacilityPhotoStore } from '../data/Editor/facilityPhoto'
import { useQualityChecklistStore } from '../data/Editor/qualityChecklist'
import { useQualityInspectionStore } from '../data/Editor/qualityInspection'
import { useQualityResultStore } from '../data/Editor/qualityResult'
import { useQualityResultPhotoStore } from '../data/Editor/qualityResultPhoto'
import { FileDownloadManager } from '../../lib/Task/fileDownloadManager'
import { filterTypeItems } from '../constant'
import { useVoiceFileStore } from '../data/Editor/voiceFile'
import { useUserStore } from './user'
import { useAuthStore } from '../data/auth'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import type { SyncAction } from '@/lib/Firebase/types'
import {
  DEFECT_PERIOD_LABEL,
  filterInputDataByTaskItems,
  getTaskDefectSetting,
  toDefectItemSummary,
  type DefectItemSummary,
} from '../function/taskDefectSetting'

const storeKey = 'tasks'

// building_type이 있으면 type 앞에 붙여서 표기 (예: '주택하자진단')
// 하자진단 과업에 사용검사 전/후가 정해져 있으면 그 앞에 붙임 (예: 'OO아파트 사용검사전 주택하자진단')
export function buildConvertedName(task: {
  year?: number
  period?: string
  name?: string
  type?: string
  building_type?: string | null
  attribute?: unknown
}): string {
  // 품질점검 과업은 내부 타입 문자열(QUALITY) 노출 없이 과업명 중심으로 표기
  if (task.type === 'QUALITY') {
    return `${task.year ?? ''} ${task.name ?? ''}`.trim()
  }
  const typeLabel = task.building_type
    ? `${task.building_type}${task.type ?? ''}`
    : (task.type ?? '')
  const defectSetting = getTaskDefectSetting(task)
  const periodLabel = defectSetting ? DEFECT_PERIOD_LABEL[defectSetting.period] : ''
  return `${task.year ?? ''} ${task.period ?? ''} ${task.name ?? ''} ${periodLabel} ${typeLabel}`
    .replace(/\s+/g, ' ')
    .trim()
}

// 메모리 최적화: currentTaskData에서 중첩 defects/defect_photos 제거
function stripNestedDefects(task: Task): number {
  let count = 0
  for (const facility of task.facilities ?? []) {
    for (const floorplan of facility.floorplans ?? []) {
      count += floorplan.defects?.length ?? 0
      floorplan.defects = undefined
    }
  }
  return count
}

export const useAllTaskStore = defineStore('all-task', () => {
  const taskList = ref<Task[]>([])
  const currentTaskData = ref<Task | null>(null)
  // SQLite 이벤트 리스너 관리
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null
  const currentTaskId = ref<string>('')
  // initTaskData 취소 제어
  let initAbortController: AbortController | null = null
  const originTaskList = ref<Task[]>([])

  //문서 작성 > 음성녹음
  const isOpenVoiceRecords = ref<boolean>(false)
  const selectedVoiceRecordTargetId = ref<string | null>(null)
  const selectedVoiceRecordItem = ref<any | null>(null)
  const selectedVoiceRecordPageItem = ref<any>(null)

  //문서 작성 > 사진 첨부
  const isOpenPhotoStore = ref<boolean>(false)
  const selectedPhotoUrl = ref<string | null>(null)
  const selectedPhotoTargetId = ref<string | null>(null)

  //문서 작성 > 시설물 사진 여러개 선택
  const currentMultiFileItem = ref<any>(null)
  const currentMultiFilePageItem = ref<any>(null)
  const currentMultiFileSelectedIds = ref<any>(null)

  // 입력항목 설정 데이터
  const userDefectInputData = ref<UserDefectInputItem[]>([])
  // 하자진단 하자항목 정의 요약 (과업 선택 id → 이름, 오프라인 캐시 함께)
  const userDefectItemSummary = ref<DefectItemSummary[]>([])
  // 현재 과업의 사용검사 전/후·하자항목 설정 (없으면 기존 과업 = 현행 동작)
  const currentTaskDefectSetting = computed(() => getTaskDefectSetting(currentTaskData.value))
  // 표시용 입력항목: 과업이 고르지 않은 하자항목 제외 (판정·출력은 userDefectInputData 그대로)
  const taskDefectInputData = computed(() =>
    filterInputDataByTaskItems(
      userDefectInputData.value,
      userDefectItemSummary.value,
      currentTaskDefectSetting.value,
    ),
  )
  // 개인설정 (기타설정) 데이터
  const userSettingConfig = ref<UserSettingConfig[]>([])
  // 개인설정 (정밀안전점검 관련)
  const testMesureForm = ref<DefectDetailItem[]>([])
  // 입력항목 프리셋 데이터
  const userPresetSetting = ref<UserDefectInputPreset[]>([])

  // 다운로드 진행률 상태
  const previousPhotoProgress = ref<number>(0)
  const floorplanProgress = ref<number>(0)
  const facilityPhotoProgress = ref<number>(0)
  const isDownloadCompleted = ref<boolean>(true)
  // 다운로드 단계: -1=비활성, 0=초기화, 1=도면+시설물사진, 2=결함사진
  const downloadPhase = ref<number>(-1)

  // 전차 등록 모드 상태
  const isPrevRegistMode = ref<boolean>(false)
  // 전차 등록 모드에서 미등록 행 선택 여부
  const isPrevUnRegistRowSelected = ref<boolean>(false)

  const taskType = computed(() => {
    if (currentTaskData.value?.type) {
      return filterTypeItems.find(item => item.name == currentTaskData.value?.type)?.value
    }
    return null
  })
  const initTaskList = async (isFirst: boolean = false): Promise<void | Task[]> => {
    if (!isFirst) return
    // 인증 상태 확인 (로그아웃 중 호출 방어)
    const authStore = useAuthStore()
    if (!authStore.isLoggedIn) return
    let localData: Task[] = []
    let mergedData: Task[] = []
    const networkStatus = await Network.getStatus()
    if (isApp()) {
      localData = (await SQLite.getLocalData(storeKey, '', null, true)) as Task[]
      // company_id 기준 필터링 (계정별 격리)
      const currentCompanyId = authStore.companyId
      if (currentCompanyId) {
        localData = localData.filter(t => t.company_id === currentCompanyId)
      }
      // 초기값은 로컬 데이터
      mergedData = localData
    }
    if (networkStatus.connected) {
      return await getTaskListServer()
        .then(async serverData => {
          if (!isApp()) {
            return Merge.sortupdatedAt(serverData)
          }
          // 3. 테이블 생성 처리
          if (!(await SQLite.checkTableExists(storeKey, serverData))) {
            return serverData
          }

          return serverData
        })
        .then(async serverData => {
          if (!isApp()) {
            return serverData
          }
          mergedData = Merge.mergeBothData(localData, serverData, storeKey)
          // 4. 데이터 병합
          return mergedData
        })
        .then(async mergedData => {
          // 5. 스토어 업데이트
          if (mergedData.length > 0) {
            const sortData = Merge.sortupdatedAt(mergedData)
            // merge 과정에서 유실될 수 있는 convertedName 재계산
            for (const task of sortData) {
              task.convertedName = buildConvertedName(task)
            }
            taskList.value = dedupeTasksById(sortData)
            if (isApp()) {
              await updateSqliteDB(taskList.value)
            }
          }
        })
        .catch(error => {
          throw error
        })
    } else {
      const sortData = Merge.sortupdatedAt(mergedData)
      // 오프라인: SQLite에서 로드한 데이터의 convertedName 재계산
      for (const task of sortData) {
        task.convertedName = buildConvertedName(task)
      }
      taskList.value = dedupeTasksById(sortData)
      return taskList.value
    }
  }

  /**
   * 메인페이지 task 목록의 id 기반 중복 제거 safety net.
   * SQLite에 동일 의미의 id가 다른 표기("557" vs "557.0")로 두 row 들어있는 케이스를
   * 메모리 표시 단계에서 한 번 더 정리한다. 같은 id는 마지막 entry로 덮어쓰기.
   */
  function dedupeTasksById(tasks: Task[]): Task[] {
    const map = new Map<string, Task>()
    for (const t of tasks) {
      // id 비교는 String 변환 후 ".0" 제거하여 통일 (안전망)
      const raw = String(t.id ?? '')
      const key = /^-?\d+\.0$/.test(raw) ? raw.slice(0, -2) : raw
      map.set(key, t)
    }
    return Array.from(map.values())
  }

  const addTaskTable = async (taskData: Task): Promise<Task> => {
    await SQLite.checkTableExists(storeKey, [taskData])
    return taskData
  }

  function getTaskListFromNow(id?: string): Task[] {
    const cloneList = cloneDeep(taskList.value)
    const filteredList = cloneList.filter(task => !task.deleted_at)
    if (id) {
      taskList.value = filteredList.filter(task => task.id.toString() != id)
    }
    return filteredList
  }

  const getTaskListServer = async (): Promise<Task[]> => {
    try {
      const datas: Task[] = await APIServer.getTaskList()
      originTaskList.value = datas

      // Promise.all을 사용하여 모든 비동기 작업을 병렬로 처리
      const tasks = datas.map(item => {
        return {
          ...item,
          id: Number(item.id),
          convertedName: buildConvertedName(item),
          facilities: [],
          task_id: Number(item.id),
          localStringId: item.id.toString(),
        } as Task
      })

      // null 값 필터링
      return Merge.sortupdatedAt(tasks.filter(task => task !== null))
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  function getFacility(facilityDatabaseId: string): Facility {
    if (isNotLoaded() || !currentTaskData.value) {
      throw new Error('EditorTaskStore is not loaded')
    }
    const facilityBranch = currentTaskData.value?.facilities.find(
      facility => facility.id.toString() == facilityDatabaseId,
    )

    if (!facilityBranch) {
      throw new Error('Facility not found')
    }
    return facilityBranch
  }

  const getTaskList = computed(() => {
    const sortedData = Merge.sortupdatedAt(taskList.value.filter(task => !task.deleted_at))
    return sortedData
  })

  const getConvertedDetailOnServerForMiniFlow = async (
    task: Task,
    dataLength?: TaskDataLength[],
  ): Promise<Task | null> => {
    if (!task) return null
    const current = task
    const returnTask: Task = {
      ...current,
      convertedName: buildConvertedName(current),
      facilities: [],
    }

    if (!current.facilities) return returnTask
    for (const facility of current.facilities) {
      if (!facility.floorplans) continue

      let shapeIds = 0
      let photoIds = 0

      const facilityData: Facility = {
        ...facility,
        id: facility.id as number,
        task_id: task.id as number,
        shapeIds: 0,
        photoIds: 0,
        floorplans: facility.floorplans,
      }

      const currentDataLength = dataLength?.find(x => x.id == facility.id)
      if (currentDataLength) {
        shapeIds = currentDataLength?.defects ?? 0
        photoIds = currentDataLength?.defect_photos ?? 0
      }

      facilityData.shapeIds = shapeIds
      facilityData.photoIds = photoIds
      returnTask.facilities.push(facilityData)
    }

    return returnTask
  }

  async function getLocalTaskData(taskId: string): Promise<Task | null> {
    const localTask = (await SQLite.getLocalData(storeKey, 'id', taskId, true)) as Task[]
    if (localTask[0]) {
      let currentFacilities = cloneDeep(localTask[0].facilities)
      if (currentFacilities.length > 0) {
        currentFacilities = currentFacilities.filter(facility => !facility.deleted_at)
      }
      return {
        ...localTask[0],
        facilities: currentFacilities,
      }
    }
    return null
  }

  async function getLocalTaskList(): Promise<Task[]> {
    if (!isApp()) return []
    const localData = (await SQLite.getLocalData(storeKey, '', null, true)) as Task[]
    const currentCompanyId = useAuthStore().companyId
    const filteredData = localData.filter(
      task => !task.deleted_at && (!currentCompanyId || task.company_id === currentCompanyId),
    )
    return Merge.sortupdatedAt(filteredData)
  }

  async function checkLocalTaskData(taskId: string): Promise<boolean> {
    const toastInstance = App.toastInstance
    const networkStatus = await Network.getStatus()
    if (networkStatus.connected) {
      return true
    }
    const localTask = await getLocalTaskData(taskId)
    if (localTask) {
      if (!localTask?.facilities?.length) {
        const errorMessage = '과업데이터를 찾을 수 없습니다. 인터넷에 연결 후 다시 시도해주세요.'
        if (toastInstance) toastInstance.add('error', errorMessage)
        return false
      }
      return true
    }
    if (toastInstance) {
      toastInstance.add(
        'error',
        '오프라인 상태에서 과업 데이터를 찾을 수 없습니다. 인터넷에 연결 후 다시 시도해주세요.',
      )
    }
    return false
  }

  const beforeSelectedNodeId = ref<string | null>(null)

  /**
   * @param signal - AbortSignal. 취소된 경우 currentTaskData 설정을 스킵한다.
   * @param options.excludeDefects - true면 중첩 defects/defect_photos 제외 (앱은 플랫 API로 별도 수신)
   */
  const getTaskDetail = async (
    taskId: string,
    signal?: AbortSignal,
    options?: { excludeDefects?: boolean },
  ): Promise<Task | null> => {
    let mergedData: Task | null = null
    const isOnline = (await Network.getStatus()).connected

    if (isOnline || !isApp()) {
      // 서버 조회 성공 여부 (로컬 병합/DB 예외를 서버 지연으로 오인하지 않기 위함)
      let detailFetched = false
      try {
        const detail: Task[] = await APIServer.getTask(taskId, options)
        detailFetched = true
        if (!detail[0]) return null
        const convertedDetail = detail[0]
        if (!convertedDetail) return null
        mergedData = convertedDetail
        if (isApp()) {
          const localTask = await getLocalTaskData(taskId)
          if (localTask) {
            const mergeData = await mergeServerToLocalServerFirst(
              [localTask as Task],
              cloneDeep([{ ...convertedDetail }]),
            )
            mergedData = mergeData[0] as Task
          } else {
            const taskData = await addTaskTable(convertedDetail)
            if (taskData) {
              const mergeData = await mergeServerToLocalServerFirst(
                [taskData],
                cloneDeep([convertedDetail]),
              )
              mergedData = mergeData[0] as Task
            } else {
              mergedData = convertedDetail
            }
          }
        }
        if (!signal || !signal.aborted) {
          currentTaskData.value = mergedData
        }
        // 기존 entry가 있으면 그 자리에 최신 detail로 replace (중복 push 방지).
        // 원래 로직은 find로 찾은 직후 push까지 해서 같은 task가 2개로 누적되었다.
        originTaskList.value = originTaskList.value.map(x =>
          x.id.toString() == taskId ? detail[0] : x,
        )
        if (mergedData) {
          await updateSqliteDB([mergedData])
        }
      } catch (apiError) {
        // 온라인이라 판단했지만 API 실패 → 오프라인 폴백
        if (isApp()) {
          // 서버 콜드스타트/일시 장애 시 홈 목록이 저장한 facilities 빈 스텁으로 폴백되어
          // 과업이 텅 빈 것처럼 보일 수 있으므로, 조용히 넘기지 않고 원인을 알린다
          console.warn('getTaskDetail 처리 실패, 로컬 데이터로 폴백:', apiError)
          // 뒤로가기 등으로 중단된 요청이나 로컬 처리 예외에는 서버 지연 토스트를 띄우지 않음
          if (!detailFetched && !signal?.aborted) {
            App.toastInstance?.add(
              'error',
              '서버 연결이 지연되어 저장된 데이터로 표시합니다. 잠시 후 과업에 다시 진입해주세요.',
            )
          }
          const taskItem = await getLocalTaskData(taskId)
          if (taskItem) {
            mergedData = taskItem
          }
          if (!signal || !signal.aborted) {
            currentTaskData.value = mergedData
          }
        } else {
          throw apiError
        }
      }
    } else {
      if (isApp()) {
        const taskItem = await getLocalTaskData(taskId)
        if (taskItem) {
          mergedData = taskItem
        }
        if (!signal || !signal.aborted) {
          currentTaskData.value = mergedData
        }
      }
    }

    return mergedData
  }

  const getTaskDetailClone = async (taskId: string): Promise<Task | null> => {
    const detail: Task[] = await APIServer.getTask(taskId)
    if (!detail[0]) return null
    let taskLength: TaskDataLength[] = []
    if (detail[0].facilities) {
      taskLength = await APIServer.getTaskDataLength(taskId)
    }
    const convertedDetail = await getConvertedDetailOnServerForMiniFlow(detail[0], taskLength)
    if (!convertedDetail) return null
    return convertedDetail
  }

  let isSyncing = false
  const updateSqliteDB = async (datas: Task[]): Promise<void> => {
    if (!isApp() || isSyncing) return
    isSyncing = true
    try {
      const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(datas, storeKey)
      await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskId.value, storeKey)
    } catch (e) {
      console.error('Error in updateSqliteDB:', e)
      throw e
    } finally {
      isSyncing = false
    }
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId - 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    // 기존 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('tasks', currentListener)
      currentListener = null
    }

    // 새 리스너 등록
    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      if (!taskId) return

      try {
        // bulk 업데이트이거나 현재 taskId가 변경된 항목에 포함된 경우에만 처리
        if (event.type === 'bulk' || event.ids.length === 0 || event.ids.includes(taskId)) {
          const localTask = await getLocalTaskData(taskId)
          if (localTask) {
            // facilities는 별도로 관리되므로 기존 값 유지
            currentTaskData.value = {
              ...localTask,
              facilities: currentTaskData.value?.facilities ?? localTask.facilities ?? [],
            }
          }
        }
      } catch (e) {
        console.error('[TaskStore] Failed to process SQLite event:', e)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated('tasks', currentListener)
  }

  /**
   * @param taskDatabaseId - 과업 ID
   * @param showLoading - 로딩 스피너 표시 여부 (호출자가 라우터 기반으로 결정)
   */
  const initTaskData = async (taskDatabaseId: string, showLoading: boolean): Promise<void> => {
    if (!taskDatabaseId) return

    // 이전 호출 취소
    if (initAbortController) {
      initAbortController.abort()
    }
    const controller = new AbortController()
    initAbortController = controller
    const { signal } = controller

    currentTaskId.value = taskDatabaseId
    setLastInSync(taskDatabaseId)

    const stateStore = useAppStateStore()
    const loadingId = showLoading ? stateStore.uniqueLoadingShow() : null

    try {
      const networkConnected = (await Network.getStatus()).connected

      const userStore = useUserStore()
      try {
        await userStore.getMe(networkConnected)
      } catch (e) {
        // 회색지대 네트워크에서 getMe 실패 시 로컬 USER_INFO 폴백 (앱 전용)
        if (!isApp()) throw e
        console.warn('getMe 실패, 로컬 사용자 정보로 폴백:', e)
        await userStore.getMe(false)
      }
      if (signal.aborted) return

      if (isApp()) {
        setupSQLiteListener(taskDatabaseId)
        await initAppEditor(taskDatabaseId, !showLoading, loadingId, signal, networkConnected)
      } else {
        await initWebEditor(taskDatabaseId, signal)
      }
    } catch (ex) {
      if (signal.aborted) return
      throw ex
    } finally {
      if (loadingId) {
        stateStore.uniqueLoadingClose(loadingId)
      }
    }
  }

  const initWebEditor = async (taskDatabaseId: string, signal: AbortSignal): Promise<void> => {
    const dataStore = useWebEditorDataStore()
    const docStore = useDocStore()
    const facilityPhotoStore = useFacilityPhotoStore()
    const floorplanStore = useFloorplanStore()
    const voiceFileStore = useVoiceFileStore()
    try {
      // 개인설정, 프리셋은 실패 허용 (로딩x)
      const silentSettingsPromise = Promise.allSettled([getUserSetting(), getUserPreset()]).then(
        () => {
          useCanvasStore().initUserSettings()
        },
      )

      const task = await getTaskDetail(taskDatabaseId, signal)
      if (signal.aborted) return
      if (task == null) {
        throw new Error('Task not found')
      }

      const defects: Defect[] = []
      const defectPhotos: DefectPhoto[] = []
      const facilityPhotos: FacilityPhoto[] = []
      const floorplans: Floorplan[] = []

      for (const facility of task.facilities) {
        const currentFacilityPhotos = cloneDeep(facility.facility_photos)
        if (currentFacilityPhotos) {
          facilityPhotos.push(...currentFacilityPhotos)
        }
        for (const floorplan of facility.floorplans) {
          const currentFloorplan = cloneDeep(floorplan)
          if (currentFloorplan) {
            floorplans.push(currentFloorplan)
          }
          const floorDefects = cloneDeep(floorplan.defects)
          if (floorDefects) {
            defects.push(...floorDefects)
            for (const defect of floorDefects) {
              const floorDefectPhotos = cloneDeep(defect.defect_photos)
              if (floorDefectPhotos) {
                defectPhotos.push(...floorDefectPhotos)
              }
            }
          }
        }
      }

      dataStore.setShapes(defects)
      dataStore.setDefectPhotos(defectPhotos)

      // 메모리 최적화: 추출 완료 후 currentTaskData에서 중첩 defects 제거
      if (currentTaskData.value) {
        stripNestedDefects(currentTaskData.value)
      }

      await Promise.all([
        floorplanStore.initData(taskDatabaseId, floorplans),
        facilityPhotoStore.initData(taskDatabaseId, facilityPhotos),
        docStore.initData(task),
        voiceFileStore.initData(taskDatabaseId),
      ])

      await silentSettingsPromise
    } catch (ex) {
      if (signal.aborted) return
      throw new Error('SetTask failed : ' + (ex instanceof Error ? ex.message : 'Unknown error'))
    }
  }

  /**
   * 과업 데이터에서 다운로드할 파일 URL을 수집
   * @param taskItem - 과업 데이터
   */
  async function collectDownloadUrls(taskItem: Task): Promise<{
    floorplanImages: string[]
    defectPhotos: string[]
    facilityImages: string[]
    tileFloorplans: Array<{ facilityName: string; floorplan: Floorplan }>
  }> {
    try {
      const shapeStore = useShapeStore()
      const defectPhotoStore = useDefectPhotoStore()
      const facilityPhotoStore = useFacilityPhotoStore()

      const floorplanImages: string[] = []
      const defectPhotoUrls = new Set<string>()
      const facilityImages: string[] = []
      const tileFloorplans: Array<{ facilityName: string; floorplan: Floorplan }> = []

      // SQLite에서 전체 shape를 한 번에 조회 (151개 개별 쿼리 대신 1회 쿼리)
      const allShapes = await shapeStore.getAllShapeRefsFromDB()
      const shapesByFloor = new Map<string, typeof allShapes>()
      for (const shape of allShapes) {
        const fid = `${shape.floorplan_id}`
        let list = shapesByFloor.get(fid)
        if (!list) {
          list = []
          shapesByFloor.set(fid, list)
        }
        list.push(shape)
      }

      // photos를 Map으로 사전 인덱싱 (O(n²) → O(n))
      const allPhotos = defectPhotoStore.getDefectPhotosByTaskId()
      const photosByShapeKey = new Map<string, DefectPhoto[]>()
      for (const photo of allPhotos) {
        const lsid = String(_.get(photo, 'localShapeId') ?? '')
        const did = String(photo.defect_id ?? '')
        const keys = new Set<string>()
        if (lsid) keys.add(lsid)
        if (did && did !== lsid) keys.add(did)
        for (const key of keys) {
          let list = photosByShapeKey.get(key)
          if (!list) {
            list = []
            photosByShapeKey.set(key, list)
          }
          list.push(photo)
        }
      }

      for (const facility of taskItem.facilities) {
        facilityImages.push(
          ...facilityPhotoStore
            .getFacilityPhotoList(facility.id.toString())
            .map((p: FacilityPhoto) => p.url),
        )

        for (const floorplan of facility.floorplans) {
          if (floorplan.file_url) {
            floorplanImages.push(floorplan.file_url)
          }

          if (floorplan.is_tiled && floorplan.tile_config && floorplan.tile_base_url) {
            tileFloorplans.push({ facilityName: facility.name, floorplan })
          }

          const floorShapes = shapesByFloor.get(floorplan.id.toString()) ?? []
          for (const shape of floorShapes) {
            const shapePhotos = photosByShapeKey.get(shape.localStringId) ?? []
            for (const p of shapePhotos) {
              if (!isPreviousPhoto(p)) {
                defectPhotoUrls.add(p.url)
              }
            }
            if (isPreviousShape(shape.previous_defect_id)) {
              for (const p of shapePhotos) {
                if (isPreviousPhoto(p)) {
                  defectPhotoUrls.add(p.url)
                }
              }
            }
          }
        }
      }

      return {
        floorplanImages,
        defectPhotos: Array.from(defectPhotoUrls),
        facilityImages,
        tileFloorplans,
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      console.error('collectDownloadUrls ERROR:', msg)
      throw e
    }
  }

  // 마지막 서버 동기화 성공 시각 (스킵 시에는 갱신하지 않음)
  let lastSyncedTaskId = ''
  let lastSyncedAt = 0
  const SKIP_SYNC_THRESHOLD_MS = 30_000

  const initAppEditor = async (
    taskDatabaseId: string,
    isSameTask: boolean,
    loadingId: string | null,
    signal: AbortSignal,
    networkConnected: boolean,
  ): Promise<void> => {
    const stateStore = useAppStateStore()
    let showDownloadProgress = false
    let backgroundPromise: Promise<unknown> | null = null

    // 동일 과업 재진입 시 서버 동기화 스킵 여부
    // 동일 과업의 마지막 서버 동기화가 30초 이내면 스킵
    const skipServerSync =
      lastSyncedTaskId === taskDatabaseId && Date.now() - lastSyncedAt < SKIP_SYNC_THRESHOLD_MS

    try {
      useDeviceEventLogStore().initData(taskDatabaseId)

      // 1. 코어 데이터 로드 (페이지 이동 시 abort signal로 조기 종료)
      const storesPromise = Promise.all([
        useShapeStore().initData(taskDatabaseId, signal, skipServerSync),
        useDefectPhotoStore().initData(taskDatabaseId, signal, skipServerSync),
        useFloorplanStore().initData(taskDatabaseId),
        useFacilityPhotoStore().initData(taskDatabaseId),
      ])

      let taskItem: Task | null
      // skipServerSync여도 currentTaskData가 없으면(clear된 경우) 폴백
      const actuallySkipped = skipServerSync && !!currentTaskData.value
      if (actuallySkipped) {
        taskItem = currentTaskData.value
      } else {
        // 중첩 defects는 어차피 stripNestedDefects로 폐기되고 플랫 API(/defects, /defect-photos)가
        // 별도 수신하므로 제외 - 서버 쿼리 비용/페이로드 대폭 절감
        taskItem = await getTaskDetail(taskDatabaseId, signal, { excludeDefects: true })
      }

      if (signal.aborted) return
      await storesPromise
      if (signal.aborted) return

      // 메모리 최적화: stores 초기화 완료 후 currentTaskData에서 중첩 defects 제거
      if (currentTaskData.value) {
        stripNestedDefects(currentTaskData.value)
      }

      if (!taskItem) {
        App.toastInstance?.add('error', '과업을 찾을 수 없습니다.')
        throw new Error('과업을 찾을 수 없습니다.')
      }
      // QUALITY 과업은 시설물/도면 없이 체크리스트 기반으로 동작하므로 시설물 부재 검사 제외
      const isQualityTaskType = taskItem.type === 'QUALITY'
      if (!taskItem.facilities.length && !networkConnected && !isQualityTaskType) {
        const msg = '과업데이터를 찾을 수 없습니다. 인터넷에 연결 후 다시 시도해주세요.'
        App.toastInstance?.add('error', msg)
        App.Router.push('/')
        throw new Error(msg)
      }

      // QUALITY 과업 전용 데이터 적재 (스냅샷 체크리스트·세션·결과·사진·협력업체)
      // 기존 SAFETY/DEFECT 로드 경로에는 영향 없음 - task.type === 'QUALITY'일 때만 실행
      if (isQualityTaskType) {
        await Promise.all([
          useQualityChecklistStore().initData(taskDatabaseId, signal),
          useQualityInspectionStore().initData(taskDatabaseId, signal, skipServerSync),
          useQualityResultStore().initData(taskDatabaseId, signal, skipServerSync),
          useQualityResultPhotoStore().initData(taskDatabaseId, signal, skipServerSync),
        ])
        if (signal.aborted) return
      }

      // 2. 스피너 전환 (일반 로딩 → 다운로드 프로그레스)
      showDownloadProgress = !isSameTask && networkConnected
      if (showDownloadProgress) {
        isDownloadCompleted.value = false
        previousPhotoProgress.value = 0
        floorplanProgress.value = 0
        facilityPhotoProgress.value = 0
        downloadPhase.value = 0
      }
      if (loadingId) stateStore.uniqueLoadingClose(loadingId)

      // 3. 백그라운드 작업 (설정 + 문서 + 음성, 실패 허용) — 진입은 기다리지 않는다(매 진입 1.6~3.1s 대기였음).
      // 설정·입력항목은 앱 시작 직후처럼 비어 있으면 로컬 사본으로 먼저 채워 바로 열리는 층·패널이 쓰게 하고,
      // 문서 작성은 docStore.isPreparing/whenReady로 준비가 끝나길 기다린다
      hydrateSettingsFromLocal(taskDatabaseId)
      backgroundPromise = Promise.allSettled([
        getInputItems(taskDatabaseId, networkConnected),
        getUserSetting(networkConnected),
        getUserPreset(networkConnected),
        useDocStore().initData(taskItem),
        useVoiceFileStore().initData(taskDatabaseId),
      ]).then(() => {
        if (signal.aborted) return
        useCanvasStore().initUserSettings()
      })

      // 4. 파일 다운로드 (도면 → 결함사진 → 시설물사진 → 타일)
      const { floorplanImages, defectPhotos, facilityImages, tileFloorplans } =
        await collectDownloadUrls(taskItem)

      const fileManager = FileDownloadManager.getInstance()
      fileManager.startNewTask(taskDatabaseId)

      const makeBatch = (
        urls: string[],
        sectionId: string,
        progressRef: typeof floorplanProgress,
      ): Promise<void> =>
        fileManager.addDownloadBatch({
          taskId: taskDatabaseId,
          tasks: urls.map(url => ({ taskId: taskDatabaseId, fileUrl: url, name: url })),
          type: 'file',
          sectionId,
          progressCallback: showDownloadProgress
            ? (_current: number, _total: number, percentage: number): void => {
                progressRef.value = percentage
              }
            : undefined,
        })

      // Phase 1: 도면 + 시설물사진 (단일 배치로 합쳐 동시 다운로드 6개 유지)
      const phase1Urls = [...floorplanImages, ...facilityImages]
      if (phase1Urls.length > 0) {
        if (showDownloadProgress) downloadPhase.value = 1
        await makeBatch(phase1Urls, 'phase1', floorplanProgress)
      }
      if (signal.aborted) return

      // Phase 2: 결함사진
      if (defectPhotos.length > 0) {
        if (showDownloadProgress) downloadPhase.value = 2
        await makeBatch(defectPhotos, 'defectPhotos', previousPhotoProgress)
      }
      if (signal.aborted) return

      // 5. 완료 (프로그레스 종료). 타일은 모든 초기화 + 백그라운드 작업이 끝난 뒤 시작 (최저 우선순위)
      // defect 2000개 이상이면 메모리 부족 우려로 타일 다운로드 스킵
      if (tileFloorplans.length > 0) {
        backgroundPromise
          .then(async () => {
            if (signal.aborted) return
            const totalShapeCount = await useShapeStore().getTotalShapeCount()
            if (signal.aborted || totalShapeCount >= 2000) return
            await fileManager.addTileDownloads(tileFloorplans, taskDatabaseId)
          })
          .catch(error => {
            console.warn('[FileDownload] 타일 도면 백그라운드 다운로드 에러:', error)
          })
      }

      if (showDownloadProgress) {
        downloadPhase.value = -1
        isDownloadCompleted.value = true
      }
      // 실제 데이터 로드가 수행된 경우 시각 기록 (캐시 사용 시 갱신 안 함)
      // 온라인인데 시설물이 비어 있으면 서버 장애 폴백(빈 스텁)일 수 있으므로 기록하지 않는다
      // — 기록하면 30초 내 재진입이 빈 currentTaskData를 재사용해 빈 화면이 반복된다
      const detailSuspicious =
        networkConnected && !isQualityTaskType && taskItem.facilities.length === 0
      if (!actuallySkipped && !detailSuspicious) {
        lastSyncedTaskId = taskDatabaseId
        lastSyncedAt = Date.now()
      }
    } catch (ex) {
      if (signal.aborted) return
      const msg = ex instanceof Error ? ex.message : String(ex)
      console.error('initAppEditor ERROR:', msg)
      if (ex instanceof Error) {
        App.toastInstance?.add('error', ex.message)
        throw ex
      }
      throw new Error('Unknown error in initAppEditor')
    } finally {
      backgroundPromise?.catch(() => {})
      if (showDownloadProgress) {
        downloadPhase.value = -1
        isDownloadCompleted.value = true
      }
    }
  }

  // 로컬 사본 JSON 읽기(깨졌으면 null)
  function readLocalJson<T>(key: string): T | null {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  }

  // 비어 있는(앱 시작 뒤 첫 진입) 설정·입력항목·프리셋을 로컬 사본으로 먼저 채운다 — 서버 응답이 오면 덮어쓴다
  function hydrateSettingsFromLocal(taskDatabaseId: string): void {
    if (userDefectInputData.value.length === 0) {
      const items = readLocalJson<UserDefectInputItem[]>(`userDefectInputData_${taskDatabaseId}`)
      if (Array.isArray(items)) userDefectInputData.value = items
    }
    if (userDefectItemSummary.value.length === 0) {
      const items = readLocalJson<DefectItemSummary[]>(`userDefectItemSummary_${taskDatabaseId}`)
      if (Array.isArray(items)) userDefectItemSummary.value = items
    }
    if (userSettingConfig.value.length === 0) {
      const config = readLocalJson<UserSettingConfig[]>('userSettingConfig')
      const form = readLocalJson<DefectDetailItem[]>('testMeasureForm')
      if (Array.isArray(config)) userSettingConfig.value = config
      if (Array.isArray(form) && testMesureForm.value.length === 0) testMesureForm.value = form
      useCanvasStore().initUserSettings()
    }
    if (userPresetSetting.value.length === 0) {
      const presets = readLocalJson<UserDefectInputPreset[]>('userDefectInputPreset')
      if (Array.isArray(presets)) userPresetSetting.value = presets
    }
  }

  // 입력항목 서버 조회 시각·진행 중 조회 (결함 패널 열 때 재조회 간격·중복 방지)
  let inputItemsFetchedAt = 0
  let inputItemsFetchedTaskId = ''
  let inputItemsInflight: Promise<unknown> | null = null
  const INPUT_ITEMS_REFRESH_INTERVAL_MS = 30_000

  // 서버 응답이 지금 값과 같으면 교체를 생략한다 - 교체하면 내용이 같아도 taskDefectInputData 재계산과
  // 입력항목 watcher(패널·시설물 사진)가 다시 돈다. 비교는 toRaw 문자열(반응형 프록시째 직렬화는 A21s 23ms)
  function applyIfChanged<T>(target: { value: T[] }, next: T[], storageKey: string): void {
    const json = JSON.stringify(next)
    if (JSON.stringify(toRaw(target.value)) !== json) target.value = next
    if (localStorage.getItem(storageKey) !== json) localStorage.setItem(storageKey, json)
  }

  async function getInputItems(
    taskDatabaseId: string,
    isConnected?: boolean,
  ): Promise<UserDefectInputItem[] | null> {
    if (isConnected ?? (await Network.getStatus()).connected) {
      const fetching = Promise.all([
        APIServer.getUserDefectInputItemList(),
        refreshDefectItemSummary(taskDatabaseId),
      ])
      inputItemsInflight = fetching
      try {
        const [inputItems] = await fetching
        applyIfChanged(userDefectInputData, inputItems, `userDefectInputData_${taskDatabaseId}`)
      } finally {
        // 실패해도 시각을 남긴다 - 서버가 안 받는 동안 결함을 누를 때마다 요청이 나가지 않게(30초 간격)
        inputItemsFetchedAt = Date.now()
        inputItemsFetchedTaskId = taskDatabaseId
        if (inputItemsInflight === fetching) inputItemsInflight = null
      }
    } else {
      const localData = readLocalJson<UserDefectInputItem[]>(
        `userDefectInputData_${taskDatabaseId}`,
      )
      const localSummary = readLocalJson<DefectItemSummary[]>(
        `userDefectItemSummary_${taskDatabaseId}`,
      )
      if (Array.isArray(localSummary)) userDefectItemSummary.value = localSummary
      if (Array.isArray(localData)) {
        userDefectInputData.value = localData
      } else {
        App.toastInstance?.add(
          'error',
          '입력항목 데이터를 찾을 수 없습니다. 인터넷을 연결 후 다시 시도해주세요.',
        )
        return null
      }
    }
    return userDefectInputData.value
  }

  // 하자진단 회사만: 정의 요약 조회 + 과업별 로컬 사본 (실패해도 입력항목 조회는 계속, 이전 값 유지)
  async function refreshDefectItemSummary(taskDatabaseId?: string): Promise<DefectItemSummary[]> {
    if (useAuthStore().company?.category !== 'DEFECT') return userDefectItemSummary.value
    try {
      const items = (await APIServer.getDefectItems()).map(toDefectItemSummary)
      if (taskDatabaseId) {
        applyIfChanged(userDefectItemSummary, items, `userDefectItemSummary_${taskDatabaseId}`)
      } else {
        userDefectItemSummary.value = items
      }
    } catch (error) {
      console.error('[getInputItems] 하자항목 정의 조회 실패:', error)
    }
    return userDefectItemSummary.value
  }

  // 앱 결함 패널 열 때: 온라인이고 마지막 조회가 30초 넘었으면 백그라운드로 다시 받는다(열기는 기다리지 않음).
  // 진행 중 조회가 있으면 겹쳐 보내지 않는다. 바뀐 게 없으면 스토어를 건드리지 않는다(applyIfChanged)
  function refreshInputItemsIfStale(taskDatabaseId: string): void {
    if (!taskDatabaseId || inputItemsInflight) return
    if (
      inputItemsFetchedTaskId === taskDatabaseId &&
      Date.now() - inputItemsFetchedAt < INPUT_ITEMS_REFRESH_INTERVAL_MS
    ) {
      return
    }
    getInputItems(taskDatabaseId, true).catch(error => {
      console.error('[getInputItems] 결함 패널 열기 재조회 실패', error)
    })
  }

  // 웹 화면용: 입력항목을 서버에서 새로 받아 표시용(과업 선택 반영) 또는 전체를 돌려준다
  async function fetchDefectInputDataForTask(filtered: boolean): Promise<UserDefectInputItem[]> {
    const [inputItems] = await Promise.all([
      APIServer.getUserDefectInputItemList(),
      filtered ? refreshDefectItemSummary() : Promise.resolve(userDefectItemSummary.value),
    ])
    if (!filtered) return inputItems
    return filterInputDataByTaskItems(
      inputItems,
      userDefectItemSummary.value,
      currentTaskDefectSetting.value,
    )
  }

  async function getUserSetting(isConnected?: boolean): Promise<UserSettingConfig[] | null> {
    if (isConnected ?? (await Network.getStatus()).connected) {
      const { config, testMeasureForm } = await APIServer.getUserSetting()
      userSettingConfig.value = config
      testMesureForm.value = testMeasureForm
      localStorage.setItem('userSettingConfig', JSON.stringify(userSettingConfig.value))
      localStorage.setItem('testMeasureForm', JSON.stringify(testMesureForm.value))
    } else {
      const localSettingConfig = localStorage.getItem('userSettingConfig')
      const localTestMeasureForm = localStorage.getItem('testMeasureForm')
      if (localSettingConfig && localTestMeasureForm) {
        userSettingConfig.value = JSON.parse(localSettingConfig)
        testMesureForm.value = JSON.parse(localTestMeasureForm)
      } else {
        App.toastInstance?.add('error', '개인설정 데이터를 찾을 수 없습니다.')
        return null
      }
    }
    return userSettingConfig.value
  }
  async function getUserPreset(isConnected?: boolean): Promise<UserDefectInputPreset[] | null> {
    if (isConnected ?? (await Network.getStatus()).connected) {
      userPresetSetting.value = await APIServer.getUserDefectInputPresetList()
      localStorage.setItem('userDefectInputPreset', JSON.stringify(userPresetSetting.value))
    } else {
      const localData = localStorage.getItem('userDefectInputPreset')
      if (localData) {
        userPresetSetting.value = JSON.parse(localData)
      } else {
        App.toastInstance?.add('error', '입력항목 프리셋 데이터를 찾을 수 없습니다.')
        return null
      }
    }
    return userPresetSetting.value
  }

  function getUserPresetLocal(): UserDefectInputPreset[] {
    const localData = localStorage.getItem('userDefectInputPreset')
    if (localData) {
      const parsedData = JSON.parse(localData)
      if (parsedData) {
        userPresetSetting.value = parsedData
        return parsedData
      }
      return []
    }
    return []
  }
  // useAllTaskStore 내부
  const addFacilityToCurrentTask = async (facility: Facility): Promise<void> => {
    if (!currentTaskData.value) return

    const numericId = Number(facility.id)

    if (isApp()) {
      // 앱 환경: SQLite + 메모리 동시 업데이트
      const localData = await getLocalTaskData(currentTaskId.value)
      if (localData) {
        const existingIndex = localData.facilities.findIndex(f => f.id === numericId)
        if (existingIndex !== -1) {
          const existing = localData.facilities[existingIndex]
          localData.facilities[existingIndex] = {
            ...existing,
            ...facility,
            floorplans: mergeFloorplanArrays(existing.floorplans || [], facility.floorplans || []),
          }
        } else {
          localData.facilities.push(facility)
        }
        await updateSqliteDB([localData])
      }
      // 메모리도 즉시 업데이트 (reloadFlow 등에서 즉시 반영)
      const cloneList = cloneDeep(currentTaskData.value)
      const memIdx = cloneList.facilities.findIndex(f => f.id === numericId)
      if (memIdx !== -1) {
        const existing = cloneList.facilities[memIdx]
        cloneList.facilities[memIdx] = {
          ...existing,
          ...facility,
          floorplans: mergeFloorplanArrays(existing.floorplans || [], facility.floorplans || []),
        }
      } else {
        cloneList.facilities.push(facility)
      }
      currentTaskData.value = cloneList
    } else {
      // 웹 환경: 메모리 직접 업데이트 (중복 체크 포함)
      const cloneList = cloneDeep(currentTaskData.value)
      const existingIndex = cloneList.facilities.findIndex(f => f.id === numericId)

      if (existingIndex !== -1) {
        const existing = cloneList.facilities[existingIndex]
        cloneList.facilities[existingIndex] = {
          ...existing,
          ...facility,
          floorplans: mergeFloorplanArrays(existing.floorplans || [], facility.floorplans || []),
        }
      } else {
        cloneList.facilities.push(facility)
      }
      currentTaskData.value = cloneList
      const docsStore = useDocStore()
      await docsStore.addNewFacilityDocs(numericId)
    }
  }

  /**
   * 특정 시설물에 도면을 추가하는 함수
   * - 중복 ID 체크를 통해 이미 존재하는 도면은 스킵
   */
  const addFloorplansToCurrentTask = async (
    facility_id: string,
    addedFloorplans: Floorplan[],
  ): Promise<void> => {
    if (!currentTaskData.value) return

    // 중복 체크를 포함한 도면 병합 함수
    const mergeFloorplansWithCheck = (
      existingFloorplans: Floorplan[],
      newFloorplans: Floorplan[],
    ): Floorplan[] => {
      const existingIds = new Set(existingFloorplans.map(f => f.id))
      const uniqueNewFloorplans = newFloorplans.filter(f => !existingIds.has(f.id))
      return [...existingFloorplans, ...uniqueNewFloorplans]
    }

    if (isApp()) {
      // 앱 환경: SQLite 업데이트
      const localData = await getLocalTaskData(currentTaskId.value)
      if (localData) {
        let hasChanges = false
        localData.facilities.forEach((facility: Facility) => {
          if (facility.id.toString() === facility_id) {
            const merged = mergeFloorplansWithCheck(facility.floorplans, addedFloorplans)
            if (merged.length !== facility.floorplans.length) {
              facility.floorplans = merged
              hasChanges = true
            }
          }
        })
        if (hasChanges) {
          await updateSqliteDB([localData])
        }
      }
    }

    // 메모리 업데이트 (웹/앱 공통)
    const cloneTaskFacilities = cloneDeep(currentTaskData.value.facilities)
    currentTaskData.value.facilities = cloneTaskFacilities.map(facility => {
      if (facility.id.toString() === facility_id) {
        facility.floorplans = mergeFloorplansWithCheck(facility.floorplans, addedFloorplans)
      }
      return facility
    })
  }

  const deleteFacilityFromCurrentTask = async (facilityId: number): Promise<void> => {
    if (!currentTaskData.value) return

    if (isApp()) {
      // 앱 환경: SQLite 업데이트
      const localData = await getLocalTaskData(currentTaskId.value)
      if (localData) {
        const findFacility = localData.facilities.find(facility => facility.id === facilityId)
        if (findFacility) {
          findFacility.deleted_at = Merge.getCurrentTime()
          await updateSqliteDB([localData])
        }
      }
    }

    // 메모리 업데이트 (웹/앱 공통)
    currentTaskData.value.facilities = currentTaskData.value.facilities.filter(
      facility => facility.id !== facilityId,
    )

    await APIServer.deleteFacilityBranch([{ id: parseInt(facilityId.toString()) }])

    const docsStore = useDocStore()
    await docsStore.deleteFacilityDocs(facilityId)
  }

  function isNotLoaded(): boolean {
    return !currentTaskData.value
  }

  async function updateFloorplanData(facility_id: string, newFloorplan: Floorplan): Promise<void> {
    if (!currentTaskData.value) return

    const currentTask = currentTaskData.value
    const numericFacilityId = Number(facility_id)
    const numericFloorplanId = Number(newFloorplan.id)

    // 1. 시설물 찾기
    const facilityIndex = currentTask.facilities.findIndex(x => x.id === numericFacilityId)
    if (facilityIndex === -1) return

    const facility = currentTask.facilities[facilityIndex]
    const floorplans = facility.floorplans || []

    // 2. 도면 찾기 (ID 기준)
    const floorplanIndex = floorplans.findIndex(fp => fp.id === numericFloorplanId)

    let updatedFloorplans: Floorplan[]

    if (floorplanIndex !== -1) {
      // [Case A] 이미 존재하는 도면 -> 기존 데이터와 병합 (기존 속성 유지)
      const existing = floorplans[floorplanIndex]
      const mergedFloorplan = { ...existing, ...newFloorplan }

      updatedFloorplans = [...floorplans]
      updatedFloorplans[floorplanIndex] = mergedFloorplan
    } else {
      // [Case B] 존재하지 않는 도면 -> 신규 추가
      updatedFloorplans = [...floorplans, newFloorplan]
    }

    // 3. 시설물 데이터 업데이트 (참조 교체로 반응성 트리거)
    currentTask.facilities[facilityIndex] = {
      ...facility,
      floorplans: updatedFloorplans,
    }

    // 4. 전체 과업 데이터 반응성 강제 트리거
    currentTaskData.value = { ...currentTask }
  }

  function getCurrentFloorplan(floorDatabaseId: string): Floorplan | undefined {
    return currentTaskData.value?.facilities
      .find(x => x.floorplans.find(y => y.id.toString() == floorDatabaseId))
      ?.floorplans.find(y => y.id.toString() == floorDatabaseId)
  }

  function getFacilityName(facilityDatabaseId: string): string {
    return (
      currentTaskData.value?.facilities.find(x => x.id.toString() == facilityDatabaseId)?.name ?? ''
    )
  }

  function getFacilityNameFloorId(floorDatabaseId: string): string | undefined {
    return currentTaskData.value?.facilities.find(x =>
      x.floorplans.find(y => y.id.toString() == floorDatabaseId),
    )?.name
  }

  function getFacilityFloorId(floorId: number): number | undefined {
    return currentTaskData.value?.facilities.find(x => x.floorplans.find(y => y.id == floorId))?.id
  }

  function getFloorplanUrl(floorDatabaseId: string): string {
    if (isNotLoaded()) {
      throw new Error('EditorTaskStore is not loaded')
    }
    const floorplan = currentTaskData.value?.facilities
      .flatMap(x => x.floorplans)
      .find(y => y.id.toString() == floorDatabaseId)

    const resourcePath = floorplan?.file_url ?? ''
    return CloudFile.ResourceUrlToSignedUrl(resourcePath)
  }

  const debouncedUpdateFacilityName = _.debounce(updateFacilityName, 350)
  async function updateFacilityName(facilityDatabaseId: string, name: string): Promise<void> {
    if (!currentTaskData.value) return
    const facility = currentTaskData.value?.facilities.find(
      x => x.id.toString() == facilityDatabaseId,
    )
    if (!facility) return

    const facilityId = facility.id
    const nameTimestamp = Merge.getCurrentTime()

    // 롤백용 원본 보존
    const prevName = facility.name
    const prevFieldsUpdatedAt = { ...facility.fields_updated_at }

    // API 호출 전에 로컬 상태를 먼저 업데이트 (optimistic update)
    // SSE 이벤트 도착 시 fields_updated_at 기반 병합에서 덮어씌워지지 않도록 타임스탬프도 갱신
    facility.name = name
    facility.fields_updated_at = {
      ...facility.fields_updated_at,
      name: nameTimestamp,
    }

    try {
      await APIServer.updateFacilityBranch([
        {
          id: facilityId,
          name: name,
          fields_updated_at: { ...facility.fields_updated_at },
        },
      ])

      if (isApp()) {
        // 앱 환경: SQLite 업데이트
        const localData = await getLocalTaskData(currentTaskId.value)
        if (localData) {
          const targetFacility = localData.facilities.find(
            x => x.id.toString() == facilityDatabaseId,
          )
          if (targetFacility) {
            targetFacility.name = name
            targetFacility.fields_updated_at = {
              ...targetFacility.fields_updated_at,
              name: nameTimestamp,
            }
            await updateSqliteDB([localData])
          }
        }
      }
      const docsStore = useDocStore()
      await docsStore.updateFacilityNameDocs(facilityId)
    } catch (error) {
      facility.name = prevName
      facility.fields_updated_at = prevFieldsUpdatedAt
      throw error
    }
  }

  const getTaskName = computed(() => currentTaskData.value?.convertedName ?? '')

  const updateTaskInfo = async (taskInfo: TaskForUpdate): Promise<void> => {
    if (!currentTaskData.value) return
    const obj = {
      ...currentTaskData.value,
      id: Number(taskInfo.id),
      name: taskInfo.name,
      type: taskInfo.type,
      period: taskInfo.period,
      year: taskInfo.year,
    } as Task
    // building_type·attribute(사용검사 전/후)가 빠지지 않게 병합한 값으로 계산
    obj.convertedName = buildConvertedName(obj)

    if (isApp()) {
      // 앱 환경: SQLite 업데이트 -> 리스너가 메모리 업데이트
      await updateSqliteDB([obj])
    } else {
      // 웹 환경: 메모리 직접 업데이트
      currentTaskData.value = obj
    }

    await APIServer.updateTaskOnly([obj])
  }

  const getCurrentFloorplans = (facilityDatabaseId: string): Floorplan[] | undefined => {
    return sortByKeys(
      currentTaskData.value?.facilities.find(x => x.id.toString() == facilityDatabaseId)
        ?.floorplans ?? [],
    )
  }

  const getTaskFacilityPhotos = async (taskId: string): Promise<FacilityPhoto[]> => {
    const photos = await APIServer.getFacilityBranchPhotoListByTaskId(taskId)
    return photos.filter(x => !x.deleted_at)
  }

  const cloneTaskData = async (
    sourceTaskId: number | string,
    targetTaskId: number | string,
    includeBrush: boolean = false,
  ): Promise<string> => {
    try {
      // 앱 환경은 SQLite(id 컬럼 TEXT)에서 로드된 task id가 문자열로 넘어온다.
      // 서버 validateFields는 number만 허용하므로 전송 전 정규화 (webeditor.ts의 Number(id) 관례와 동일)
      const sourceId = Number(sourceTaskId)
      const targetId = Number(targetTaskId)
      if (!Number.isFinite(sourceId) || !Number.isFinite(targetId)) {
        throw new Error(`잘못된 과업 ID (source: ${sourceTaskId}, target: ${targetTaskId})`)
      }

      await APIServer.useTaskDataAsPrevious(sourceId, targetId, includeBrush)
      // 서버에서 시설물/도면/결함이 새로 생성되었으므로 initAppEditor의 30초 sync 스킵 캐시를 무효화.
      // 무효화하지 않으면 복제 직후 initTaskData가 복제 전 currentTaskData를 재사용해 빈 화면이 표시된다
      lastSyncedTaskId = ''
      lastSyncedAt = 0
      await initTaskData(targetId.toString(), true)
      return '과업 데이터를 복사했습니다.'
    } catch (error) {
      console.error('cloneTaskData error:', error)
      return '과업 데이터 복사에 실패했습니다.'
    }
  }

  const getCurrentTaskAttributes = computed(() => currentTaskData.value?.attribute)
  const updateCurrentTaskAttributes = async (
    attributes: Record<string, unknown>,
  ): Promise<void> => {
    if (!currentTaskData.value) return
    const newAttributes = {
      ...currentTaskData.value.attribute,
      facilities: attributes.facilities,
    }
    const newFieldsUpdatedAt = {
      ...(currentTaskData.value?.fields_updated_at ?? {}),
      attribute: Merge.getCurrentTime(),
    }

    // store, sqlite 먼저 업데이트
    if (isApp()) {
      const localData = await getLocalTaskData(currentTaskId.value)
      if (localData) {
        localData.attribute = newAttributes
        localData.fields_updated_at = newFieldsUpdatedAt
        await updateSqliteDB([localData])
      }
    } else {
      currentTaskData.value.attribute = newAttributes
      currentTaskData.value.fields_updated_at = newFieldsUpdatedAt
    }

    // 서버 업데이트
    await APIServer.updateTaskAttributes([
      {
        id: Number(currentTaskData.value.id),
        attribute: newAttributes,
        fields_updated_at: newFieldsUpdatedAt,
      },
    ])
  }
  const debouncedUpdateCurrentTaskAttributes = _.debounce(updateCurrentTaskAttributes, 350)

  // 하자진단 사용검사 전/후·하자항목 설정 저장 (attribute 병합 + fields_updated_at.attribute 스탬프).
  // 앱은 SQLite 사본의 attribute를 기준으로 병합한다(메모리 값이 늦게 반영됐을 수 있음)
  const updateCurrentTaskDefectSetting = async (setting: {
    defect_period: 'PRE' | 'POST'
    defect_item_ids: number[]
  }): Promise<void> => {
    if (!currentTaskData.value) return
    const localData = isApp() ? await getLocalTaskData(currentTaskId.value) : null
    const baseAttribute = (localData ?? currentTaskData.value).attribute ?? {}
    const newAttributes = { ...baseAttribute, ...setting }
    const newFieldsUpdatedAt = {
      ...((localData ?? currentTaskData.value).fields_updated_at ?? {}),
      attribute: Merge.getCurrentTime(),
    }

    // 서버가 거절하면 로컬도 바꾸지 않는다 (과업 정보 변경은 온라인 전제)
    await APIServer.updateTaskAttributes([
      {
        id: Number(currentTaskData.value.id),
        attribute: newAttributes,
        fields_updated_at: newFieldsUpdatedAt,
      },
    ])

    if (localData) {
      localData.attribute = newAttributes
      localData.fields_updated_at = newFieldsUpdatedAt
      localData.convertedName = buildConvertedName(localData)
      await updateSqliteDB([localData])
    }
    // 앱은 SQLite 리스너도 갱신하지만, 패널·이름이 바로 따라오게 메모리도 맞춘다
    currentTaskData.value.attribute = newAttributes
    currentTaskData.value.fields_updated_at = newFieldsUpdatedAt
    currentTaskData.value.convertedName = buildConvertedName(currentTaskData.value)
  }

  const getCurrentAllFloorNumbering = (facility_id: number): boolean => {
    if (!currentTaskData.value) return false
    const findAttribute = currentTaskData.value.attribute
    if (!findAttribute) return false
    const allFloorNumberingFacilityIds = findAttribute?.all_floor_numbering_facility_ids ?? []
    return allFloorNumberingFacilityIds.includes(facility_id)
  }
  const updateAllFloorNumbering = async (facility_id: number, value: boolean): Promise<void> => {
    if (!currentTaskData.value) return
    const facility = currentTaskData.value.facilities.find(x => x.id == facility_id)
    if (!facility) return
    const findAttribute = currentTaskData.value.attribute
    if (!findAttribute) return

    let allFloorNumberingFacilityIds = findAttribute?.all_floor_numbering_facility_ids ?? []
    if (value) {
      allFloorNumberingFacilityIds.push(facility_id)
    } else {
      allFloorNumberingFacilityIds = allFloorNumberingFacilityIds.filter(
        (x: number) => x !== facility_id,
      )
    }

    const currentFieldsUpdate = {
      ...(currentTaskData.value?.fields_updated_at ?? {}),
      attribute: Merge.getCurrentTime(),
    }

    const attributes = {
      ...findAttribute,
      all_floor_numbering_facility_ids: allFloorNumberingFacilityIds,
      fields_updated_at: currentFieldsUpdate,
    }
    await APIServer.updateTaskAttributes([
      {
        id: Number(currentTaskData.value.id),
        fields_updated_at: currentFieldsUpdate,
        attribute: attributes,
      },
    ])

    if (isApp()) {
      // 앱 환경: SQLite 업데이트 -> 리스너가 메모리 업데이트
      const localData = await getLocalTaskData(currentTaskId.value)
      if (localData) {
        localData.attribute = attributes
        localData.fields_updated_at = currentFieldsUpdate
        await updateSqliteDB([localData])
      }
    } else {
      // 웹 환경: 메모리 직접 업데이트
      currentTaskData.value.attribute = attributes
      currentTaskData.value.fields_updated_at = currentFieldsUpdate
    }
  }

  const updateCreateShapeData = async (changedData: Defect): Promise<void> => {
    const currentTaskId = currentTaskData.value?.id
    if (!changedData || !currentTaskId) return
    // 전차 shape의 보라색이 일반 결함 생성 스타일을 오염시키지 않도록 스킵
    if (isPreviousShape(changedData.previous_defect_id)) return
    const changedObj: Record<string, number | string | undefined> = {
      color: changedData.color,
      balloon_scale: changedData.shape_data?.balloonScale,
      point_shape_scale: changedData.shape_data?.pointShapeScale,
      balloon_fill_color: changedData.shape_data?.balloonFillColor,
      balloon_font_color: changedData.shape_data?.balloonFontColor,
      balloon_font_scale: changedData.shape_data?.balloonFontScale,
      area_shape_border_thickness_scale: changedData.shape_data?.areaBorderThicknessScale,
      connected_line_thickness_scale: changedData.shape_data?.connectedLineThicknessScale,
      balloon_border_color: changedData.shape_data?.color,
    }

    // 일괄 편집에서 크기/색상만 부분 적용될 수 있어 기존 기본값과 병합한다
    const previousObj = getOriginShapeData(String(currentTaskId)) ?? {}
    const updateObj: Record<string, number | string | undefined> = { ...previousObj }
    Object.entries(changedObj).forEach(([key, value]) => {
      if (value !== undefined) updateObj[key] = value
    })

    //로컬스토리지에 저장
    localStorage.setItem(`originShapeData_${currentTaskId}`, JSON.stringify(updateObj))

    //캔버스 전역설정도 업데이트 (스케일 0은 유효값이므로 truthy가 아닌 undefined/빈값으로 판정)
    const canvasStore = useCanvasStore()
    Object.entries(changedObj).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        canvasStore.setCreateStyles(key, value)
      }
    })
  }

  const getOriginShapeData = (taskId: string): OriginShapeData | undefined => {
    const originShapeData = localStorage.getItem(`originShapeData_${taskId}`)
    if (!originShapeData) return undefined
    try {
      return JSON.parse(originShapeData)
    } catch {
      return undefined
    }
  }

  const updateInspectColumns = async (columns: string[]): Promise<void> => {
    if (!currentTaskData.value) return
    const findAttribute = currentTaskData.value.attribute ?? {}

    const attributes = {
      ...findAttribute,
      inspect_columns: columns,
    }
    await APIServer.updateTaskAttributes([
      {
        ...currentTaskData.value,
        id: Number(currentTaskData.value.id),
        attribute: attributes,
      },
    ])

    if (isApp()) {
      // 앱 환경: SQLite 업데이트 -> 리스너가 메모리 업데이트
      const localData = await getLocalTaskData(currentTaskId.value)
      if (localData) {
        localData.attribute = attributes
        await updateSqliteDB([localData])
      }
    } else {
      // 웹 환경: 메모리 직접 업데이트
      currentTaskData.value.attribute = attributes
    }
  }
  const debouncedUpdateAllFloorNumbering = _.debounce(updateAllFloorNumbering, 350)

  const getTaskNameFunc = (): string => {
    return currentTaskData.value?.convertedName ?? ''
  }

  const isLoadedFloor = ref<boolean>(false)
  const setIsLoadedFloor = (value: boolean): void => {
    isLoadedFloor.value = value
  }

  function getTaskFacilityName(facilityId: number): { taskName: string; facilityName: string } {
    const task = currentTaskData.value
    const facility = currentTaskData.value?.facilities.find(x => x.id == facilityId)

    if (!task || !facility) {
      throw new Error('과업 혹은 시설물 정보를 찾을 수 없습니다.')
    }
    return { taskName: task.name, facilityName: facility.name }
  }

  function getDirectionDrawGuideVisible(): boolean {
    const taskId = currentTaskData.value?.id
    if (!taskId) return false
    const localState = localStorage.getItem(`directionDrawGuideVisible_${taskId}`)
    if (!localState) return false
    return localState === 'true'
  }

  function setDirectionDrawGuideVisible(visible: boolean): void {
    const taskId = currentTaskData.value?.id
    if (!taskId) return
    localStorage.setItem(`directionDrawGuideVisible_${taskId}`, visible.toString())
  }

  function clearAllDocState(): void {
    isOpenPhotoStore.value = false
    isOpenVoiceRecords.value = false
    selectedVoiceRecordTargetId.value = null
    selectedVoiceRecordItem.value = null
    selectedVoiceRecordPageItem.value = null
    selectedPhotoUrl.value = null
    selectedPhotoTargetId.value = null
    currentMultiFileItem.value = null
    currentMultiFilePageItem.value = null
    currentMultiFileSelectedIds.value = null
  }

  function getBeforeTasks(prevTaskId: string): Task[] {
    return taskList.value.filter(task => task.id.toString() == prevTaskId)
  }

  function allClear(): void {
    // 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('tasks', currentListener)
      currentListener = null
    }
    taskList.value = []
    originTaskList.value = []
    clearCurrentTaskData()
  }
  function clearCurrentTaskData(): void {
    // 진행 중인 initTaskData 취소
    if (initAbortController) {
      initAbortController.abort()
      initAbortController = null
    }
    currentTaskData.value = null
    currentTaskId.value = ''
    isOpenVoiceRecords.value = false
    isOpenPhotoStore.value = false
    // 다운로드 프로그레스 해제
    downloadPhase.value = -1
    isDownloadCompleted.value = true
  }
  /**
   * 도면 배열을 ID 기준으로 병합하는 함수
   * - updated_at 타임스탬프 기반으로 최신 데이터 판단
   * - 로컬에서 수정 중인 데이터(modifiedFromLocal=true)는 보호
   */
  const mergeFloorplanArrays = (oldList: Floorplan[], newList: Floorplan[]): Floorplan[] => {
    const map = new Map<number, Floorplan>()

    // 두 타임스탬프 비교 (새 데이터가 더 최신인지 확인)
    const isNewerData = (newUpdatedAt?: string, oldUpdatedAt?: string): boolean => {
      if (!newUpdatedAt) return false
      if (!oldUpdatedAt) return true
      return new Date(newUpdatedAt).getTime() > new Date(oldUpdatedAt).getTime()
    }

    // 기존 데이터 등록
    oldList.forEach(f => map.set(Number(f.id), f))

    // 새 데이터 병합 (타임스탬프 기반)
    newList.forEach(f => {
      const id = Number(f.id)
      const existing = map.get(id)
      if (existing) {
        // 로컬에서 수정 중인 데이터는 보호
        if (existing.modifiedFromLocal) {
          return // 스킵
        }

        // 새 데이터가 더 최신인 경우에만 병합
        if (isNewerData(f.updated_at, existing.updated_at)) {
          map.set(id, { ...existing, ...f })
        }
        // 기존 데이터가 더 최신이면 그대로 유지
      } else {
        // 신규 데이터 추가
        map.set(id, f)
      }
    })

    return Array.from(map.values())
  }
  /**
   * SSE를 통해 수신된 Task 데이터를 처리하는 함수
   * - 로컬에서 이미 처리된 데이터는 스킵하거나 병합
   * - updated_at 타임스탬프 기반으로 최신 데이터 판단
   * - 앱 환경: SQLite에만 저장 → 리스너가 메모리 업데이트 (SSoT 패턴)
   * - 웹 환경: 메모리 직접 업데이트
   */
  async function handleRealtimeUpdate(
    action: SyncAction,
    dataList: Partial<Task>[],
  ): Promise<void> {
    if (!dataList || dataList.length === 0) return
    // 두 타임스탬프 비교 헬퍼
    const isNewerTimestamp = (dataTimestamp?: string, existingTimestamp?: string): boolean => {
      if (!dataTimestamp) return false
      if (!existingTimestamp) return true
      return new Date(dataTimestamp).getTime() > new Date(existingTimestamp).getTime()
    }

    // data에 업데이트할 필드가 하나라도 있는지 확인
    const hasNewerFields = (
      dataFieldsUpdatedAt?: FieldsUpdatedAt,
      existingFieldsUpdatedAt?: FieldsUpdatedAt,
    ): boolean => {
      if (!dataFieldsUpdatedAt) return false
      if (!existingFieldsUpdatedAt) return true

      for (const [field, dataTimestamp] of Object.entries(dataFieldsUpdatedAt)) {
        const existingTimestamp = existingFieldsUpdatedAt[field]
        if (!existingTimestamp || isNewerTimestamp(dataTimestamp, existingTimestamp)) {
          return true
        }
      }
      return false
    }

    // 필드별 타임스탬프 비교하여 최신 값만 선택하여 병합
    const mergeByFieldTimestamp = (data: Partial<Task>, existing: Task): Partial<Task> => {
      const dataFieldsUpdatedAt = data.fields_updated_at || {}
      const existingFieldsUpdatedAt = existing.fields_updated_at || {}

      const mergedData: Partial<Task> = {}
      const mergedFieldsUpdatedAt: FieldsUpdatedAt = { ...existingFieldsUpdatedAt }

      for (const [field, dataTimestamp] of Object.entries(dataFieldsUpdatedAt)) {
        const existingTimestamp = existingFieldsUpdatedAt[field]

        if (!existingTimestamp || isNewerTimestamp(dataTimestamp, existingTimestamp)) {
          if (field in data) {
            const dataRecord = data as Record<string, unknown>
            const mergedRecord = mergedData as Record<string, unknown>
            mergedRecord[field] = dataRecord[field]
          }
          mergedFieldsUpdatedAt[field] = dataTimestamp
        }
      }

      mergedData.fields_updated_at = mergedFieldsUpdatedAt
      return mergedData
    }

    // 서버 데이터를 스토어 규격(Task)으로 변환하는 헬퍼 함수
    const transformToStoreTask = (data: Partial<Task>, existing?: Task): Task => {
      if (existing) {
        const mergedFields = mergeByFieldTimestamp(data, existing)
        const merged = {
          ...existing,
          ...mergedFields,
          id: Number(data.id),
          task_id: Number(data.id),
          localStringId: data.id?.toString() ?? existing.localStringId ?? '',
          facilities: existing.facilities,
          deleted_at: data.deleted_at ?? existing.deleted_at ?? null,
        }
        // convertedName 재계산
        merged.convertedName = buildConvertedName(merged)
        return merged as Task
      }

      // 기존 데이터가 없으면 그대로 사용 (새 데이터)
      const newTask = {
        ...data,
        id: Number(data.id),
        task_id: Number(data.id),
        localStringId: data.id?.toString() ?? '',
        facilities: data.facilities ?? [],
        deleted_at: data.deleted_at ?? null,
        convertedName: buildConvertedName(data),
      }
      return newTask as Task
    }

    let hasChanges = false
    const tasksToSave: Task[] = []

    if (action === 'INSERT') {
      for (const data of dataList) {
        if (!data.id) continue
        // 이미 삭제된 데이터는 INSERT하지 않음
        if (data.deleted_at) continue
        const numericId = Number(data.id)
        const existingIndex = taskList.value.findIndex(x => x.id == numericId)

        if (existingIndex === -1) {
          // 신규 데이터 추가
          const newTask = transformToStoreTask(data)
          // 메모리 직접 업데이트 (앱/웹 공통 - Firebase SSE 데이터는 서버 최신 데이터)
          taskList.value.push(newTask)
          tasksToSave.push(newTask)
          hasChanges = true
        } else {
          // 이미 존재하는 경우 (로컬에서 먼저 추가됨) - 필드별 병합
          const existing = taskList.value[existingIndex]
          if (hasNewerFields(data.fields_updated_at, existing.fields_updated_at)) {
            const mergedTask = transformToStoreTask(data, existing)
            // 메모리 직접 업데이트 (앱/웹 공통)
            taskList.value[existingIndex] = mergedTask
            tasksToSave.push(mergedTask)
            hasChanges = true
          }
          // Firebase에 최신 필드가 없으면 스킵 (로컬 데이터 유지)
        }
      }
    } else if (action === 'UPDATE') {
      for (const data of dataList) {
        if (!data.id) continue
        const numericId = Number(data.id)
        const index = taskList.value.findIndex(x => x.id == numericId)

        if (index !== -1) {
          const existingTask = taskList.value[index]

          // Firebase 데이터에 최신 필드가 하나라도 있는 경우에만 업데이트
          if (hasNewerFields(data.fields_updated_at, existingTask.fields_updated_at)) {
            const mergedTask = transformToStoreTask(data, existingTask)

            // 메모리 직접 업데이트 (앱/웹 공통)
            taskList.value[index] = mergedTask

            // 현재 보고 있는 상세 페이지의 데이터가 업데이트된 경우 처리 (웹 환경만)
            if (!isApp() && currentTaskData.value && currentTaskData.value.id == numericId) {
              currentTaskData.value = {
                ...currentTaskData.value,
                ...mergedTask,
                facilities: currentTaskData.value.facilities, // facilities는 별도 처리
              }
            }
            tasksToSave.push(mergedTask)
            hasChanges = true
          }
          // Firebase에 최신 필드가 없으면 스킵
        } else if (currentTaskData.value && currentTaskData.value.id == numericId) {
          // 목록 없이 열린 과업(앱 재시작 뒤 편집기 경로 복원 등): 목록에 없다고 버리면 다른 기기의
          // 과업 변경(사용검사 전/후·하자항목 선택 등)이 재진입 전까지 반영되지 않는다
          const base = (isApp() ? await getLocalTaskData(numericId.toString()) : null) ?? {
            ...currentTaskData.value,
          }
          if (hasNewerFields(data.fields_updated_at, base.fields_updated_at)) {
            const mergedTask = transformToStoreTask(data, base as Task)
            if (isApp()) {
              // 앱: SQLite 저장 → 리스너가 currentTaskData 갱신(facilities 유지)
              tasksToSave.push(mergedTask)
              hasChanges = true
            } else {
              currentTaskData.value = {
                ...currentTaskData.value,
                ...mergedTask,
                facilities: currentTaskData.value.facilities,
              }
            }
          }
        }
      }
    } else if (action === 'DELETE') {
      for (const data of dataList) {
        if (!data.id) continue
        const numericId = Number(data.id)
        const index = taskList.value.findIndex(x => x.id == numericId)

        if (index !== -1) {
          const existing = taskList.value[index]
          // 이미 삭제 처리된 경우 스킵
          if (existing.deleted_at) continue

          const deletedTask = {
            ...existing,
            deleted_at: data.deleted_at ?? Merge.getCurrentTime(),
          }

          // 메모리 직접 업데이트 (앱/웹 공통)
          taskList.value[index] = deletedTask

          // 현재 작업 중인 과업이 삭제된 경우 처리 (앱/웹 공통)
          if (currentTaskData.value && currentTaskData.value.id == numericId) {
            App.toastInstance?.add('warning', '서버에서 현재 과업이 삭제되었습니다.')
            App.Router.push('/')
          }
          tasksToSave.push(deletedTask)
          hasChanges = true
        }
      }
    }

    // 변경사항이 있을 때 처리
    if (hasChanges) {
      // 재정렬 (앱/웹 공통)
      taskList.value = Merge.sortupdatedAt(taskList.value)
      // 앱 환경: SQLite에도 저장 (영속성)
      if (isApp() && tasksToSave.length > 0) {
        await updateSqliteDB(tasksToSave)
      }
    }
  }

  /**
   * SSE를 통해 수신된 Facility 데이터를 처리하는 함수
   * - 로컬에서 이미 처리된 데이터는 스킵하거나 병합
   * - fields_updated_at 기반으로 필드별 최신 데이터 판단
   * - 앱 환경: SQLite에만 저장 → 리스너가 메모리 업데이트 (SSoT 패턴)
   * - 웹 환경: 메모리 직접 업데이트
   */
  async function handleRealtimeUpdateFacility(
    action: SyncAction,
    dataList: Partial<Facility>[],
  ): Promise<void> {
    if (!dataList || dataList.length === 0 || !currentTaskData.value) return

    const numericTaskId = Number(currentTaskData.value.id)
    const filteredDataList = dataList.filter(x => Number(x.task_id) === numericTaskId)

    if (filteredDataList.length === 0) return

    // 두 타임스탬프 비교 헬퍼
    const isNewerTimestamp = (dataTimestamp?: string, existingTimestamp?: string): boolean => {
      if (!dataTimestamp) return false
      if (!existingTimestamp) return true
      return new Date(dataTimestamp).getTime() > new Date(existingTimestamp).getTime()
    }

    // data에 업데이트할 필드가 하나라도 있는지 확인
    const hasNewerFields = (
      dataFieldsUpdatedAt?: FieldsUpdatedAt,
      existingFieldsUpdatedAt?: FieldsUpdatedAt,
    ): boolean => {
      if (!dataFieldsUpdatedAt) return false
      if (!existingFieldsUpdatedAt) return true

      for (const [field, dataTimestamp] of Object.entries(dataFieldsUpdatedAt)) {
        const existingTimestamp = existingFieldsUpdatedAt[field]
        if (!existingTimestamp || isNewerTimestamp(dataTimestamp, existingTimestamp)) {
          return true
        }
      }
      return false
    }

    // 필드별 타임스탬프 비교하여 최신 값만 선택하여 병합
    const mergeByFieldTimestamp = (
      data: Partial<Facility>,
      existing: Facility,
    ): Partial<Facility> => {
      const dataFieldsUpdatedAt = data.fields_updated_at || {}
      const existingFieldsUpdatedAt = existing.fields_updated_at || {}

      const mergedData: Partial<Facility> = {}
      const mergedFieldsUpdatedAt: FieldsUpdatedAt = { ...existingFieldsUpdatedAt }

      for (const [field, dataTimestamp] of Object.entries(dataFieldsUpdatedAt)) {
        const existingTimestamp = existingFieldsUpdatedAt[field]

        if (!existingTimestamp || isNewerTimestamp(dataTimestamp, existingTimestamp)) {
          if (field in data) {
            const dataRecord = data as Record<string, unknown>
            const mergedRecord = mergedData as Record<string, unknown>
            mergedRecord[field] = dataRecord[field]
          }
          mergedFieldsUpdatedAt[field] = dataTimestamp
        }
      }

      mergedData.fields_updated_at = mergedFieldsUpdatedAt
      return mergedData
    }

    // 앱/웹 공통: currentTaskData에서 직접 작업
    // facilities는 메모리에서 관리되므로 currentTaskData 사용
    const workingTask: Task = cloneDeep(currentTaskData.value)
    const newFacilityIds = new Set<number>()
    const updatedFacilityIds = new Set<number>()
    const deletedFacilityIds = new Set<number>()

    let isChanged = false

    for (const data of filteredDataList) {
      const numericId = Number(data.id)
      const index = workingTask.facilities.findIndex(f => f.id === numericId)

      if (action === 'INSERT') {
        if (index === -1) {
          // 신규 데이터 추가
          const newFacility = {
            ...data,
            floorplans: [],
            facility_photos: [],
            id: numericId,
            task_id: Number(data.task_id),
          } as Facility
          workingTask.facilities.push(newFacility)
          newFacilityIds.add(numericId)
          isChanged = true
        } else {
          // 이미 존재하는 경우 (로컬에서 먼저 추가됨) - 필드별 병합
          const existing = workingTask.facilities[index]
          if (hasNewerFields(data.fields_updated_at, existing.fields_updated_at)) {
            const mergedFields = mergeByFieldTimestamp(data, existing)
            workingTask.facilities[index] = {
              ...existing,
              ...mergedFields,
              id: numericId,
              task_id: Number(data.task_id),
              // 하위 배열들은 기존 것 유지 (별도 SSE로 처리됨)
              floorplans: existing.floorplans,
              facility_photos: existing.facility_photos,
            } as Facility
            isChanged = true
            updatedFacilityIds.add(numericId)
          }
          // Firebase에 최신 필드가 없으면 스킵
        }
      } else if (action === 'UPDATE') {
        if (index !== -1) {
          const existing = workingTask.facilities[index]

          // Firebase 데이터에 최신 필드가 하나라도 있는 경우에만 업데이트
          if (hasNewerFields(data.fields_updated_at, existing.fields_updated_at)) {
            const mergedFields = mergeByFieldTimestamp(data, existing)
            workingTask.facilities[index] = {
              ...existing,
              ...mergedFields,
              // 하위 배열들은 기존 것 유지 (별도 SSE로 처리됨)
              floorplans: existing.floorplans,
              facility_photos: existing.facility_photos,
            } as Facility
            isChanged = true
            updatedFacilityIds.add(numericId)
          }
          // Firebase에 최신 필드가 없으면 스킵
        }
      } else if (action === 'DELETE') {
        if (index !== -1) {
          const existing = workingTask.facilities[index]
          // 이미 삭제 처리된 경우 스킵
          if (existing.deleted_at) continue

          workingTask.facilities = workingTask.facilities.filter(f => f.id !== numericId)
          isChanged = true
          deletedFacilityIds.add(numericId)
          // 웹 환경에서 현재 보고 있는 시설물이 삭제된 경우 Flow 페이지로 이동
          if (!isApp()) {
            const webFacilityStore = useWebEditorFacilityStore()
            const selectedFacilityId = webFacilityStore.selectedFacilityDatabaseId

            if (selectedFacilityId && selectedFacilityId === numericId.toString()) {
              App.toastInstance?.add('warning', '서버에서 현재 시설물이 삭제되었습니다.')
              // 선택된 시설물과 층을 클리어하여 Flow.vue가 표시되도록 함
              webFacilityStore.clearSelection()
            }
          }
        }
      }
    }

    if (isChanged) {
      // 메모리 직접 업데이트 (앱/웹 공통 - Firebase SSE 데이터는 서버 최신 데이터)
      workingTask.facilities = [...workingTask.facilities]
      currentTaskData.value = { ...workingTask }

      if (newFacilityIds.size > 0) {
        const docsStore = useDocStore()
        for (const facilityId of newFacilityIds) {
          await docsStore.addNewFacilityDocs(facilityId)
        }
      }

      if (updatedFacilityIds.size > 0) {
        const docsStore = useDocStore()
        for (const facilityId of updatedFacilityIds) {
          await docsStore.updateFacilityNameDocs(facilityId)
        }
      }

      if (deletedFacilityIds.size > 0) {
        const docsStore = useDocStore()
        for (const facilityId of deletedFacilityIds) {
          await docsStore.deleteFacilityDocs(facilityId)
        }
      }

      if (isApp()) {
        // 앱 환경: SQLite에도 저장 (영속성)
        await updateSqliteDB([workingTask])
      }
    }
  }

  return {
    getTaskList,
    getCurrentFloorplans,
    deleteFacilityFromCurrentTask,
    initTaskList,
    initTaskData,
    updateFloorplanData,
    getTaskDetail,
    getLocalTaskData,
    getLocalTaskList,
    checkLocalTaskData,
    clearAllDocState,
    addFacilityToCurrentTask,
    addFloorplansToCurrentTask,
    updateTaskInfo,
    currentTaskData,
    currentTaskId,
    isNotLoaded,
    getFacilityName,
    getFacilityNameFloorId,
    getFacilityFloorId,
    getFloorplanUrl,
    getCurrentFloorplan,
    updateFacilityName,
    getUserSetting,
    getUserPreset,
    getInputItems,
    refreshInputItemsIfStale,
    refreshDefectItemSummary,
    fetchDefectInputDataForTask,
    updateCurrentTaskDefectSetting,
    getUserPresetLocal,
    debouncedUpdateFacilityName,
    isOpenPhotoStore,
    selectedPhotoUrl,
    selectedPhotoTargetId,
    currentMultiFileItem,
    currentMultiFilePageItem,
    currentMultiFileSelectedIds,
    getTaskName,
    getTaskFacilityPhotos,
    getTaskDetailClone,
    cloneTaskData,
    debouncedUpdateCurrentTaskAttributes,
    getCurrentTaskAttributes,
    updateCurrentTaskAttributes,
    beforeSelectedNodeId,
    getTaskNameFunc,
    getFacility,
    debouncedUpdateAllFloorNumbering,
    getCurrentAllFloorNumbering,
    isLoadedFloor,
    setIsLoadedFloor,
    userDefectInputData,
    userDefectItemSummary,
    currentTaskDefectSetting,
    taskDefectInputData,
    userSettingConfig,
    getDirectionDrawGuideVisible,
    setDirectionDrawGuideVisible,
    clearCurrentTaskData,
    // 다운로드 진행률 상태
    previousPhotoProgress,
    floorplanProgress,
    facilityPhotoProgress,
    isDownloadCompleted,
    downloadPhase,
    getTaskFacilityName,
    updateInspectColumns,
    updateCreateShapeData,
    getOriginShapeData,
    isPrevRegistMode,
    isPrevUnRegistRowSelected,
    getTaskListFromNow,
    taskType,
    testMesureForm,
    isOpenVoiceRecords,
    selectedVoiceRecordTargetId,
    selectedVoiceRecordItem,
    selectedVoiceRecordPageItem,
    getBeforeTasks,
    allClear,
    handleRealtimeUpdate,
    handleRealtimeUpdateFacility,
  }
})
